import "../global.css";

import { BricolageGrotesque_400Regular } from "@expo-google-fonts/bricolage-grotesque/400Regular";
import { BricolageGrotesque_600SemiBold } from "@expo-google-fonts/bricolage-grotesque/600SemiBold";
import { BricolageGrotesque_700Bold } from "@expo-google-fonts/bricolage-grotesque/700Bold";
import { QueryClientProvider } from "@tanstack/react-query";
import { useFonts } from "expo-font";
import { Observe, ObserveRoot } from "expo-observe";
import { type ErrorBoundaryProps, Stack, usePathname } from "expo-router";
import { useEffect, useState } from "react";
import { Platform } from "react-native";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import { KeyboardProvider } from "react-native-keyboard-controller";

import { AdminHostGuard } from "@/components/admin/AdminHostGuard";
import { AppErrorScreen } from "@/components/AppErrorScreen";
import { NotificationTapBridge } from "@/components/NotificationTapBridge";
import { DialogHost } from "@/components/ui/DialogHost";
import { iconFont } from "@/components/ui/iconFont";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { reportCaughtError } from "@/lib/analytics/reportError";
import { isPublicRoutePath } from "@/lib/auth/publicRoutePaths";
import { queryClient, useQueryAppState } from "@/lib/platform/queryClient";
import { claimStaleChunkReload } from "@/lib/platform/staleChunk";
import { useVendorChunkReady } from "@/lib/platform/vendorChunk";
import { NATIVE_HEADER_TITLE_STYLE } from "@/theme/fonts";
import { ThemeProvider, useThemeColors } from "@/theme/theme";

// EAS Observe (TRO-433). Configure at module scope: `configure` throws after the app mounts, and
// the `expo-router` integration must register before the first screen mounts to measure cold-start
// navigation. This true root is the earliest point every route passes through.
//
// Expo Go sends no metrics, and debug builds send none because `dispatchInDebug` is left off, so
// local work never skews production percentiles. Development and production builds report
// normally.
Observe.configure({ integrations: { "expo-router": true } });

// The `QueryClientProvider` below is shared by `(app)` and `browse`. Query keys are namespaced per
// hook, so sharing cannot collide, and `browse` needs `useQuery` with no other provider above it.
//
// `(app)` sits beneath every route, except on a web cold open of a public page: it is a separate
// chunk on web (`scripts/web-route-chunks.js`), and holding it beneath the page would download the
// vendor app for nothing and send Back to vendor sign-in.
const INITIAL_ROUTE =
  Platform.OS === "web" && isPublicRoutePath(globalThis.location?.pathname ?? "")
    ? undefined
    : "(app)";
export const unstable_settings = { anchor: INITIAL_ROUTE };

/**
 * The root navigator, headerless by default: `(app)` runs its own Stack with the app bar, and
 * `browse` owns its whole viewport.
 *
 * `account-deletion` shows a header on native only. It can be pushed from the You tab, where a
 * headerless screen would have no visible Back. On web it is a public page linked from the App
 * Store listing with its own `<h1>`, and a web header would add a second level-1 heading.
 *
 * Must sit inside `ThemeProvider` for header colors, and honors Reduce Motion like the `(app)`
 * stack, since `account-deletion` is pushed through this navigator.
 */
export function RootStack() {
  const c = useThemeColors();
  const reduced = useReducedMotion();
  return (
    <Stack
      screenOptions={{
        animation: reduced ? "none" : "default",
        headerShown: false,
        headerBackButtonDisplayMode: "minimal",
        headerShadowVisible: false,
        headerStyle: { backgroundColor: c.bg },
        headerTintColor: c.foreground,
        headerTitleStyle: NATIVE_HEADER_TITLE_STYLE,
      }}
    >
      <Stack.Screen
        name="account-deletion"
        options={{ headerShown: Platform.OS !== "web", title: "Delete account" }}
      />
    </Stack>
  );
}

/**
 * The root layout (#340), kept thin: no ClerkProvider, AuthRedirectGate or other provider that
 * assumes a signed-in vendor. Those live in `(app)/_layout.tsx`, which wraps only the `(app)` group.
 * `browse/[eventId].tsx`, the Pull Sheet customer browse page (ADR-0017), is a sibling of `(app)`,
 * so the auth gate cannot reach it: no `ClerkProvider` mounts around it. A path check inside the
 * gate would not work, because `useAuth()` and `useSession()` throw without a `ClerkProvider`.
 *
 * `global.css` is imported here so NativeWind classes resolve for `browse` too.
 */
function RootLayout() {
  useQueryAppState();
  // Wait for fonts before ThemeProvider mounts and hides the splash (see theme.tsx), so the splash
  // never lifts on system fonts. A load error still renders with the platform font rather than
  // holding the splash forever.
  const [fontsLoaded, fontError] = useFonts({
    BricolageGrotesque_400Regular,
    BricolageGrotesque_600SemiBold,
    BricolageGrotesque_700Bold,
    ...iconFont,
  });
  useEffect(() => {
    if (fontError) console.error("[fonts] load failed, falling back to system font:", fontError);
  }, [fontError]);
  const vendorChunkReady = useVendorChunkReady();
  if ((!fontsLoaded && !fontError) || !vendorChunkReady) return null;
  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <KeyboardProvider>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <AdminHostGuard>
              <RootStack />
            </AdminHostGuard>
            {
              /* Beside the navigator: every web confirm and notice renders here, and public routes
                raise them as well as `(app)`. */
            }
            <DialogHost />
            {/* Beside the navigator too: a tapped push can open a public route as well as `(app)`. */}
            <NotificationTapBridge />
          </ThemeProvider>
        </QueryClientProvider>
      </KeyboardProvider>
    </GestureHandlerRootView>
  );
}

/**
 * `ObserveRoot.wrap` starts the session and times the cold start, so it wraps this root rather than
 * `(app)/_layout.tsx`: public routes are siblings of `(app)` (see `mobile/AGENTS.md`), and a
 * customer opening a Pull Sheet QR link is a real cold start. `src/hooks/useMarkInteractive.ts`
 * defines which routes report when they become interactive.
 *
 * Only the default export is wrapped. `ErrorBoundary` stays a separate named export so expo-router
 * still discovers it.
 */
export default ObserveRoot.wrap(RootLayout);

/**
 * Root error boundary, picked up by expo-router as a named export. It catches a render throw
 * anywhere, including `browse`, without any provider below it; `(app)/_layout.tsx` has its own.
 */
export function ErrorBoundary({ error, retry }: ErrorBoundaryProps) {
  const pathname = usePathname();
  // Unmounting the root navigator resets pathname; keep the route that actually crashed.
  const [isPublicRoute] = useState(() => isPublicRoutePath(pathname));
  const [reloading] = useState(() => claimStaleChunkReload(error));
  useEffect(() => {
    if (reloading) {
      window.location.reload();
      return;
    }
    if (isPublicRoute) return;
    reportCaughtError(error, { boundary: "root" });
  }, [error, isPublicRoute, reloading]);
  if (reloading) return null;
  return <AppErrorScreen message={error.message} onRetry={() => retry()} />;
}
